import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clapperboard, Newspaper, Play } from "lucide-react";
import { useLang, OG_LOCALES, prefixFor } from "@/lib/i18n";
import { api, fileUrl, imgUrl } from "@/lib/api";
import { cleanDisplayText } from "@/lib/articleText";
import { gazaDateTimeArticle } from "@/lib/dateGaza";
import { fetchReels } from "@/lib/reels";
import ArticleReelPlayer from "@/components/reels/ArticleReelPlayer";
import { dedupeHead } from "@/lib/seoMeta";
import { siteName } from "@/lib/siteIdentity";

const setMeta = (attr, key, value) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", value);
};

/** Indexable watch page: /video/{slug} (ar) and /en/video/{slug}. The video is the main, visible content — no tap/swipe
 *  needed — with title/description in the page language, per-language canonical, reciprocal hreflang and VideoObject. */
export default function VideoPage() {
    const { slug } = useParams();
    const { t, lang, p } = useLang();
    const [a, setA] = useState(null);
    // err: null | 404 | 410 (definitive: the story does not exist / was removed) | "transient" (network, 5xx, 429 — retryable)
    const [err, setErr] = useState(null);
    const [more, setMore] = useState([]);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let alive = true;
        setA(null); setErr(null);
        api.get(`/public/articles/${encodeURIComponent(slug)}`)
            .then((r) => { if (alive) setA(r.data?.article || r.data); })
            .catch((e) => { if (!alive) return; const s = e?.response?.status; setErr(s === 404 || s === 410 ? s : "transient"); });
        return () => { alive = false; };
    }, [slug, attempt]);
    useEffect(() => {   // definitive 404/410 only: noindex + real status for pre-render layers. Transient errors never touch <head>.
        if (err !== 404 && err !== 410) return;
        const mk = (name, content) => { const el = document.createElement("meta"); el.name = name; el.content = content; document.head.appendChild(el); return el; };
        const els = [mk("robots", "noindex, nofollow"), mk("prerender-status-code", String(err))];
        document.querySelectorAll('link[rel="alternate"][hreflang], link[rel="canonical"]').forEach((n) => n.remove());   // a missing page has no canonical/alternates
        document.title = `${err} | ${t("siteName")}`;
        return () => els.forEach((el) => el.remove());   // cleared on retry success or when navigating to another story
    }, [err, t]);
    useEffect(() => {
        let alive = true;
        fetchReels({}).then(({ items }) => alive && setMore(items.filter((x) => x.slug !== slug).slice(0, 6))).catch(() => {});
        return () => { alive = false; };
    }, [slug, lang]);

    useEffect(() => {
        if (!a) return;
        const origin = window.location.origin;
        const title = cleanDisplayText(a.title || "");
        const description = (cleanDisplayText(a.excerpt || a.content || "") || title).slice(0, 300);
        const arHref = `${origin}/video/${a.slug}`, enHref = `${origin}${prefixFor(lang)}/video/${a.slug}`;
        const hasEn = !!a.translation?.available;
        const url = lang !== "ar" && hasEn ? enHref : arHref;
        const thumb = fileUrl(a.video_poster_url || a.cover_image || "/api/public/og-image.png");
        document.title = `${title} | ${t("siteName")}`;
        window.prerenderReady = true;
        setMeta("name", "description", description);
        setMeta("property", "og:title", title); setMeta("property", "og:description", description); setMeta("property", "og:type", "video.other"); setMeta("property", "og:url", url); setMeta("property", "og:image", thumb);
        if (a.video_web_url) { setMeta("property", "og:video", fileUrl(a.video_web_url)); setMeta("property", "og:video:type", "video/mp4"); }
        setMeta("name", "twitter:card", "player"); setMeta("name", "twitter:title", title); setMeta("name", "twitter:image", thumb);
        setMeta("property", "og:locale", OG_LOCALES[lang] || "ar_AR"); setMeta("property", "og:locale:alternate", lang !== "ar" ? "ar_AR" : "en_US");
        setMeta("property", "og:site_name", siteName(lang));
        dedupeHead();
        // Never emit noindex for a published story: a crawler pre-render snapshot (cached for days) would freeze it.
        // While the web rendition is still being prepared the watch page canonicalises to the article page instead.
        document.querySelector('meta[name="robots"]')?.remove();
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
        canonical.href = a.video_web_url ? url : `${origin}${lang !== "ar" && hasEn ? prefixFor(lang) : ""}/article/${a.slug}`;   // incomplete EN → Arabic watch page
        document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
        const alts = [["ar", arHref], ["x-default", arHref]];
        for (const L of (a.translation?.languages || []).filter((x) => x !== "ar")) alts.push([L, `${origin}/${L}/video/${a.slug}`]);
        for (const [hl, href] of alts) { const l = document.createElement("link"); l.rel = "alternate"; l.hreflang = hl; l.href = href; document.head.appendChild(l); }
        const ld = {
            "@context": "https://schema.org", "@type": "VideoObject", "name": title, "description": description,
            "thumbnailUrl": [thumb], "uploadDate": a.published_at || a.created_at, "dateModified": a.updated_at || a.published_at,
            "contentUrl": a.video_web_url ? fileUrl(a.video_web_url) : fileUrl(a.video_url), "embedUrl": url, "url": url, "mainEntityOfPage": url,
            "inLanguage": lang, "isFamilyFriendly": true,
            "author": { "@type": "Person", "name": lang !== "ar" ? "Ali Salem" : "علي سالم", "url": `${origin}${prefixFor(lang)}/about` },
            "publisher": { "@type": "Organization", "name": siteName(lang), "url": `${origin}${prefixFor(lang)}/about`, "logo": { "@type": "ImageObject", "url": `${origin}/api/public/og-image.png`, "width": 1200, "height": 630 } },
            ...(a.video_duration ? { "duration": `PT${Math.round(a.video_duration)}S` } : {}),
        };
        let ldEl = document.getElementById("ld-videoobject");
        if (!ldEl) { ldEl = document.createElement("script"); ldEl.type = "application/ld+json"; ldEl.id = "ld-videoobject"; document.head.appendChild(ldEl); }
        ldEl.textContent = JSON.stringify(ld);
        return () => { document.getElementById("ld-videoobject")?.remove(); document.querySelector('meta[name="robots"]')?.remove(); };
    }, [a, lang, t]);

    if (err === "transient") {   // connection / server hiccup: plain message + retry, no 404 and no robots directive
        return (
            <div className="mx-auto py-16 text-center" style={{ maxWidth: 640 }} data-testid="video-unavailable">
                <p className="font-bold text-brand-black dark:text-gray-100">{t("transient")}</p>
                <p className="text-gray-600 dark:text-gray-300 mt-2 text-sm">{t("transientDesc")}</p>
                <button type="button" onClick={() => setAttempt((n) => n + 1)} className="inline-flex items-center gap-2 mt-6 bg-brand-red text-white px-4 py-2 rounded-full text-sm font-bold" data-testid="video-retry">{t("retry")}</button>
            </div>
        );
    }
    if (err) {
        return (
            <div className="mx-auto py-16 text-center" style={{ maxWidth: 640 }} data-testid={err === 410 ? "video-gone" : "video-not-found"}>
                <div className="font-display font-black text-5xl text-brand-red mb-3" dir="ltr">{err === 410 ? "410" : "404"}</div>
                <p className="text-gray-600 dark:text-gray-300">{err === 410 ? t("goneDesc") : t("notFoundBody")}</p>
                <Link to={`${p}/reels`} className="inline-flex items-center gap-2 mt-6 bg-brand-red text-white px-4 py-2 rounded-full text-sm font-bold">{t("reels")}</Link>
            </div>
        );
    }
    if (!a) return <div className="mx-auto my-8 rounded-lg bg-[#11191c] animate-pulse" style={{ aspectRatio: "9 / 16", maxWidth: 430 }} data-testid="video-page-loading" />;
    if (!a.video_url) {   // not a video story → the article itself is the right page
        return <div className="mx-auto py-16 text-center" style={{ maxWidth: 640 }} data-testid="video-none"><Link to={`${p}/article/${a.slug}`} className="text-brand-red font-bold underline">{t("readFull")}</Link></div>;
    }
    const title = cleanDisplayText(a.title || "");
    const description = cleanDisplayText(a.excerpt || "") || "";
    return (
        <main className="mx-auto px-4 py-6 md:py-8 grid gap-6 md:grid-cols-[minmax(0,430px)_1fr] md:items-start" style={{ maxWidth: 1040 }} dir={lang !== "ar" ? "ltr" : "rtl"} data-testid="video-page">
            <figure className="m-0" data-testid="video-page-player">
                <ArticleReelPlayer article={a} />
            </figure>
            <section>
                <h1 className="font-display font-black text-xl sm:text-2xl leading-[1.6] m-0 mb-3" style={{ textWrap: "balance" }} data-testid="video-page-title">{title}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4"><time dateTime={a.published_at}>{gazaDateTimeArticle(a.published_at || a.created_at, lang)}</time> · {lang !== "ar" ? "Ali Salem" : "علي سالم"}</p>
                {description && <p className="text-base leading-[1.9] text-gray-800 dark:text-gray-200 mb-5" data-testid="video-page-desc">{description}</p>}
                <div className="flex flex-wrap gap-2 mb-8">
                    <Link to={`${p}/article/${a.slug}`} className="inline-flex items-center gap-2 bg-brand-red text-white px-4 py-2 rounded-full text-sm font-bold no-underline" data-testid="video-page-article-link"><Newspaper className="w-4 h-4" /> {t("readFull")}</Link>
                    <Link to={`${p}/reels?v=${a.id}`} className="inline-flex items-center gap-2 border border-gray-300 dark:border-gray-600 px-4 py-2 rounded-full text-sm font-bold no-underline" data-testid="video-page-reels-link"><Clapperboard className="w-4 h-4" /> {t("reels")}</Link>
                </div>
                {more.length > 0 && (
                    <nav aria-label={t("reelsOtherVideos")} data-testid="video-page-more">
                        <h2 className="text-base md:text-lg font-bold mb-3">{t("reels")}</h2>
                        <ul className="grid grid-cols-3 gap-2 list-none p-0 m-0">
                            {more.map((r) => (
                                <li key={r.id}><Link to={`${p}/video/${r.slug}`} className="block rounded-md overflow-hidden bg-gray-100 dark:bg-[#122023] relative no-underline" style={{ aspectRatio: "9 / 16" }}>
                                    {(r.video_poster_url || r.cover_image) && <img src={imgUrl(r.video_poster_url || r.cover_image, 320)} alt={(lang !== "ar" && r.translation?.title) || r.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />}
                                    <span className="absolute inset-x-0 bottom-0 p-1.5 text-[11px] leading-snug font-bold text-white line-clamp-2 bg-gradient-to-t from-black/80 to-transparent">{(lang !== "ar" && r.translation?.title) || r.title}</span>
                                    <Play className="absolute top-2 end-2 w-4 h-4 text-white drop-shadow" />
                                </Link></li>
                            ))}
                        </ul>
                    </nav>
                )}
            </section>
        </main>
    );
}
